//////////////////
// IMPORTS
//////////////////
// libraries
import * as React from "react";
import { useDispatch } from "react-redux";
import { createRefetchContainer } from "react-relay";
// event-summary
import { EventSummary as s } from "./EventSummary.styles";
import { eventSummaryFragments, refetchQuery } from "./EventSummary.gql";
// other components
import { EventSummaryColorCategory } from "@components/event-summary-color-category";
import { SmartHoldDetails } from "@clockwise/web-commons/src/ui/smart-hold-details";
// util
import { min, max } from "@clockwise/client-commons/src/constants/time-slot";
import { ZonedMoment } from "@util/time-zone.util";
import { createEventParentRelayIdFromOrgRelayId } from "@util/event.util";
import {
  isFocusTimeSmartHold,
  isLunchSmartHold,
  isSmartHoldPaused,
  isTeamCalendarSyncEvent,
  isTravelTimeSmartHold,
} from "@clockwise/client-commons/src/util/event";
// ui
import { H1 } from "@ui";
import { EventTimeAndRecurrence } from "./EventTimeAndRecurrence";
import { RelayRefetchProp } from "react-relay";
import * as ISchema from "@schema";
import { AutopilotFormSaveOnEdit } from "@components/autopilot-form";
import { queryParams } from "@constants/site.constant";
import { EFlex, Tags } from "@src/util/event-tag.util";
import { findGoalByName } from "@src/util/goal.util";
import { getValue } from "@src/util/errorable.util";
import pluralize from "pluralize";
import { setSettingsDialogOpen } from "@actions/chrome-extension.actions";
import { toggleExpanded } from "@actions/settings.actions";
import { getDurationMinutes } from "@clockwise/web-commons/src/util/duration.util";
import { DurationPicker } from "@components/duration-picker";
import { createTagForMutation } from "@clockwise/client-commons/src/util/event-tag";
import { addUserTags } from "@mutations";
import { getCurrentEnvironment } from "@src/state/relay-environment";
import { snackBarOpen } from "@actions/snack-bar.actions";
import { track, TrackingEvents } from "@clockwise/web-commons/src/util/analytics.util";

//////////////////
// COMPONENT
//////////////////
type SmartHoldDetailsProps = {
  org: ISchema.IOrg;
  event: ISchema.IEvent;
  smartHoldEventInfo?: ISchema.ISmartHoldEventInfo | null;
};
export interface IContainer {
  user: ISchema.IUser;
  org: ISchema.IOrg;
  event: ISchema.IEvent;
  open: boolean;
  calendarId: string;
  originEvent?: ISchema.IEvent;
  hideColorSettings?: boolean;
  onClick?: () => void;
  canAutopilot?: boolean;
  eventParent?: ISchema.IEventParent;
  smartHoldEventInfo?: ISchema.ISmartHoldEventInfo | null;
  canReschedule: boolean;
  attendeeNotInDomain?: boolean;
  organizerNotClockwiseUser?: boolean;
  isAllDayEvent?: boolean;
}

export interface IProps extends IContainer {
  relay: RelayRefetchProp;
}

const LunchDetails = ({ org, isFixed }: { org: ISchema.IOrg; isFixed: boolean }) => {
  const dispatch = useDispatch();

  const lunchGoals = findGoalByName(org?.userGoals, "Lunch");
  const lunchOptions =
    ((lunchGoals?.options.specificOptions as unknown) as ISchema.IGoalWithinDayOptions) || null;
  const minDuration = lunchOptions?.minimumDuration ?? 0;
  const maxDuration = lunchOptions?.idealDuration ?? 0;
  const minTime = min?.[lunchOptions?.minTimeSlot ?? ""];
  const maxTime = max[lunchOptions?.maxTimeSlot ?? ""];
  const missingSpecifics =
    (minDuration === 0 && maxDuration === 0) || (minTime === "" && maxTime === "");

  if (missingSpecifics) {
    return null;
  }

  const duration =
    minDuration === maxDuration ? `${minDuration} mins` : `${minDuration} - ${maxDuration} mins`;
  const description = duration + ` • ${minTime} - ${maxTime}`;

  const handleSettingsClick = () => {
    dispatch(setSettingsDialogOpen(true));
    dispatch(toggleExpanded("expandLunch"));
  };

  return (
    <SmartHoldDetails
      label={isFixed ? "Lunch time fixed" : "Clockwise managed Lunch"}
      description={description}
      onClickSettings={handleSettingsClick}
      isFixed={isFixed}
    />
  );
};

const FocusTimeDetails = ({ org, isFixed }: { org: ISchema.IOrg; isFixed: boolean }) => {
  const dispatch = useDispatch();

  const focusTimeGoals = getValue(org?.shadowCalendarSettingsErrorable);
  const ftHours = (focusTimeGoals?.focusTimeIdealMinutesPerWeek || 0) / 60;
  const description =
    !focusTimeGoals?.focusTimeSyncAll && ftHours > 0
      ? `Scheduling up to ${ftHours} ${pluralize("hour", ftHours)} per week`
      : "Scheduling all available hours";

  const handleSettingsClick = () => {
    dispatch(setSettingsDialogOpen(true));
    dispatch(toggleExpanded("expandFocusTime"));
  };

  return (
    <SmartHoldDetails
      label={isFixed ? "Focus Time fixed" : "Clockwise Focus Time"}
      description={description}
      onClickSettings={handleSettingsClick}
      isFixed={isFixed}
    />
  );
};

const TravelTimeDetails = ({ event, org, smartHoldEventInfo }: SmartHoldDetailsProps) => {
  const dispatch = useDispatch();
  const duration = getDurationMinutes(event.startTime?.millis, event.endTime?.millis);
  const originEvent = getValue(smartHoldEventInfo)?.originEvent || null;
  const isYourCalendar = getValue(event.organizer)?.urnValue === org.primaryOrgCalendar;

  const handleSettingsClick = () => {
    dispatch(setSettingsDialogOpen(true));
    dispatch(toggleExpanded("expandTravelTime"));
  };

  const onEditTravelTimeSuccess = (durationString: string) => {
    let msg = `The travel time event will be updated to ${durationString} minutes`;
    if (!!originEvent?.title) {
      const title = originEvent.title;
      const beforeAfter = smartHoldEventInfo?.isPreTravelTime ? "before" : "after";
      msg =
        `The travel time ${beforeAfter} "${title}" will be updated to ${durationString} minutes,` +
        ` please wait a few moments for the change to occur.`;
    }
    dispatch(snackBarOpen(msg));
  };

  const onEditTravelTimeFailure = () => {
    dispatch(snackBarOpen("Sorry, there was a problem updating your travel time event"));
  };

  const editTravelTimeEvent = (duration: number) => {
    if (!originEvent) {
      onEditTravelTimeFailure();
      return;
    }

    const eventParentRelayId = createEventParentRelayIdFromOrgRelayId(
      org.id,
      originEvent.eventKey?.externalEventId,
    );

    if (!eventParentRelayId) {
      onEditTravelTimeFailure();
      return;
    }

    const durationString = duration.toString();
    const tagName = smartHoldEventInfo?.isPreTravelTime ? Tags.PreTravelTime : Tags.PostTravelTime;
    const tagPrePost = createTagForMutation(tagName, durationString);
    const tagManual = createTagForMutation(Tags.TravelTimeManualOverride);
    const overrideTagName = smartHoldEventInfo?.isPreTravelTime
      ? Tags.PreTravelTimeManualOverride
      : Tags.PostTravelTimeManualOverride;
    const overrideTagManual = createTagForMutation(overrideTagName);

    addUserTags(
      getCurrentEnvironment(),
      org.id,
      eventParentRelayId,
      [tagPrePost, tagManual, overrideTagManual],
      () => onEditTravelTimeSuccess(durationString),
      onEditTravelTimeFailure,
    );

    track(TrackingEvents.EVENT.EDIT_TRAVEL_TIME_EVENT);
  };

  const action = (
    <>
      Hold
      <div style={{ background: "#E6E6E6", borderRadius: 4, margin: "0px 7px" }}>
        <DurationPicker
          onDurationChange={editTravelTimeEvent}
          durations={[15, 30, 45, 60]}
          defaultDuration={duration}
          maxDuration={120}
          disabled={false}
          showLabel={false}
          disableUnderline
        />
      </div>
      of travel time
    </>
  );

  return (
    <SmartHoldDetails
      label="Clockwise travel time"
      action={originEvent && isYourCalendar ? action : undefined}
      description="Clockwise is holding travel time for external meetings"
      onClickSettings={handleSettingsClick}
      isFixed={false}
    />
  );
};

const SmartHoldFlexibilityPanel = ({ org, event, smartHoldEventInfo }: SmartHoldDetailsProps) => {
  const isFixed = isSmartHoldPaused(event.annotatedEvent);
  const isLunch = event && isLunchSmartHold(event);
  const isFocusTime = event && isFocusTimeSmartHold(event);
  const isTravelTime = event && isTravelTimeSmartHold(event);

  if (isLunch) {
    return <LunchDetails org={org} isFixed={isFixed} />;
  } else if (isFocusTime) {
    return <FocusTimeDetails org={org} isFixed={isFixed} />;
  } else if (isTravelTime) {
    return <TravelTimeDetails event={event} org={org} smartHoldEventInfo={smartHoldEventInfo} />;
  }

  return null;
};

const EventSummaryCmpt = ({
  event,
  originEvent,
  user,
  hideColorSettings,
  org,
  calendarId,
  onClick,
  canAutopilot,
  eventParent,
  attendeeNotInDomain,
  organizerNotClockwiseUser,
  isAllDayEvent,
  smartHoldEventInfo,
  relay,
  canReschedule,
}: IProps) => {
  const isClickable = !!onClick;
  const containerStyle = isClickable ? s.containerWithClick : undefined;
  const isAllDaySameStartAndEndTime =
    event.isAllDay && event.startTime.millis === event.endTime.millis;
  const startTime = isAllDaySameStartAndEndTime
    ? new ZonedMoment(event.startTime.dateOrDateTime)
    : event.startTime && new ZonedMoment(event.startTime.millis);
  const endTime = isAllDaySameStartAndEndTime
    ? new ZonedMoment(event.endTime.dateOrDateTime)
    : event.endTime && new ZonedMoment(event.endTime.millis);
  let title = event.title ? event.title : "(No title)";

  // For personal events, pull the title off the originEvent if available
  if (originEvent && originEvent.title) {
    title = originEvent.title || "(No title)";
  }

  if (event.scrubbedEvent) {
    title = "Event";
  } else if (event.locked) {
    title = "Private Event";
  }

  let viaClockwise = false;
  const VIA_CLOCKWISE = "(via Clockwise)";
  if (title.endsWith(VIA_CLOCKWISE)) {
    title = title.substring(0, title.length - VIA_CLOCKWISE.length);
    viaClockwise = true;
  }

  const showTeamName = isTeamCalendarSyncEvent(event);

  // should autopilot be enabled on mount
  const searchParams = new URLSearchParams(window.location.search);
  const apEnableOnMount = searchParams.has(queryParams.apEnable);
  const apFlexDayOnMount = searchParams.has(queryParams.apFlexDay);
  const apFlexWeekOnMount = searchParams.has(queryParams.apFlexWeek);
  const apRoomFlexOnMount = searchParams.has(queryParams.apFlexRoom);

  const canEditColorCategory =
    canReschedule && !showTeamName && !hideColorSettings && !event.scrubbedEvent && !event.locked;

  React.useEffect(() => {
    if (!event) {
      return;
    }

    const calendarIds = event.attendees?.map((a) => a.urnValue) || [];
    if (event.organizer?.urnValue && calendarIds.includes(event.organizer.urnValue)) {
      calendarIds.push(event.organizer.urnValue);
    }

    if (calendarIds) {
      relay.refetch({
        calendarIds: calendarIds.slice(0, 15),
        orgRelayId: org.id,
      });
    }
  }, []);

  const onClickContainer = () => {
    !!onClick && onClick();
  };

  // UPDATED SIDEBAR BELOW
  return (
    <div style={containerStyle} onClick={() => onClickContainer()}>
      <H1
        style={{
          ...s.h2,
          paddingBottom: 0,
        }}
        className="fs-mask"
      >
        {title}
        {viaClockwise && (
          <span>
            <br />
            (via Clockwise)
          </span>
        )}
      </H1>
      <EventTimeAndRecurrence
        org={org}
        event={event}
        startTime={startTime}
        endTime={endTime}
        isAllDay={event.isAllDay}
        calendarId={calendarId}
      />
      {canEditColorCategory && <EventSummaryColorCategory org={org} event={event} user={user} />}
      {eventParent && (
        <AutopilotFormSaveOnEdit
          org={org}
          user={user}
          event={event}
          eventParent={eventParent}
          canAutopilot={!!canAutopilot}
          canReschedule={canReschedule}
          attendeeNotInDomain={!!attendeeNotInDomain}
          organizerNotClockwiseUser={!!organizerNotClockwiseUser}
          isAllDayEvent={!!isAllDayEvent || event?.isAllDay}
          calendarId={calendarId}
          enableOnMount={apEnableOnMount}
          setTimeFlexOnMount={
            apFlexWeekOnMount ? EFlex.Week : apFlexDayOnMount ? EFlex.Day : undefined
          }
          setRoomFlexOnMount={apRoomFlexOnMount}
          surface="Sidepanel"
        />
      )}
      <SmartHoldFlexibilityPanel org={org} event={event} smartHoldEventInfo={smartHoldEventInfo} />
    </div>
  );
};

//////////////////
// RELAY
//////////////////
export const EventSummary = createRefetchContainer(
  EventSummaryCmpt,
  eventSummaryFragments,
  refetchQuery,
);
